import React, { useMemo } from 'react';
import {
    Typography,
    Grid,
    Box,
    useMediaQuery,
    useTheme,
} from '@material-ui/core';
import clsx from 'clsx';

import {
    Hero,
    ContentImage,
    Container,
    Button,
    ShadowCard,
    ReviewSlide,
    Slider,
} from '~/components/ui';
import { VideoPlayer } from '~/components/media';

const impression = `${process.env.GATSBY_IMAGES}linksImage/link2.png`;
const alignersCase = `${process.env.GATSBY_IMAGES}linksImage/link8.png`;
const smile = `${process.env.GATSBY_IMAGES}linksImage/link6.png`;
const affordable = `${process.env.GATSBY_IMAGES}patients/affordable.png`;
const affordableWebP = `${process.env.GATSBY_IMAGES}patients/affordable.webp`;

const testimonial1 = `${process.env.GATSBY_IMAGES}patients/testimonial_1.png`;
const testimonial2 = `${process.env.GATSBY_IMAGES}patients/testimonial_2.png`;
const testimonial3 = `${process.env.GATSBY_IMAGES}patients/testimonial_3.png`;
const videoPreview = `${process.env.GATSBY_IMAGES}video-preview.png`;
const tooth = `${process.env.GATSBY_IMAGES}patients/tooth.jpg`;
const printing = `${process.env.GATSBY_IMAGES}patients/3dprinting.png`;
const printingWebP = `${process.env.GATSBY_IMAGES}patients/3dprinting.webp`;
const aligners = `${process.env.GATSBY_IMAGES}patients/aligners.jpg`;

import styles from './styles';

export const Patient = () => {
    const classes = styles();
    const theme = useTheme();
    const isLg = useMediaQuery(theme.breakpoints.down('lg'));
    const isMd = useMediaQuery(theme.breakpoints.down('md'));
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isXs = useMediaQuery(theme.breakpoints.down('xs'));

    const works = [
        {
            description: 'Your doctor takes impressions of your teeth.',
            image: impression,
        },
        {
            description:
                'We use our state of the art process to make perfectly clear aligners.',
            image: alignersCase,
        },
        {
            description:
                'Your doctor tracks your progress and within 1 year, you have a brand new smile!',
            image: smile,
        },
    ];

    const renderedWorks = useMemo(() => {
        return works.map((work, index) => {
            return (
                <Grid
                    item
                    lg={4}
                    md={4}
                    sm={12}
                    xs={12}
                    className={classes.work}
                    key={index}
                >
                    <ShadowCard className={classes.workCard}>
                        <Box
                            className={classes.workImage}
                            style={{ backgroundImage: `url(${work.image})` }}
                        />
                        <Typography
                            variant="body1"
                            className={classes.workDescription}
                        >
                            {work.description}
                        </Typography>
                        <Typography variant="h4" className={classes.workIndex}>
                            {index + 1}
                        </Typography>
                    </ShadowCard>
                </Grid>
            );
        });
    }, [works]);

    const reviews = [
        {
            image: testimonial1,
            author: 'Rebecca Laurence',
            service: 'Crossbite - 20 steps',
            text:
                'I wanted my teeth straighter, but I didn’t want braces. My Orthosnap aligners are so comfortable that sometimes I forget that I even have them on.',
        },
        {
            image: testimonial2,
            author: 'Steven Campbell',
            service: 'Crossbite - 20 steps',
            text:
                'I’m so glad I made the investment in Orthosnap! It’s so easy – all I had to do was remove my aligners when I ate. Now, 6 months later, I have a perfect smile!',
        },
        {
            image: testimonial3,
            author: 'Eliana Gomez',
            service: 'Crossbite - 20 steps',
            text:
                "I always wanted to have straight teeth, but didn't think I could afford it. Orthosnap's pricing is great. I never thought my smile would look this good. Plus, no one even knew I was wearing them. I couldn’t be happier!",
        },
    ];

    const sliderItems = useMemo(() => {
        return reviews.map((review, index) => {
            return (
                <ReviewSlide
                    key={index}
                    image={review.image}
                    service={review.service}
                    text={review.text}
                    author={review.author}
                />
            );
        });
    }, [reviews]);

    const slidesMargin = useMemo(() => {
        if (isXs) {
            return 16;
        } else if (isMobile) {
            return 40;
        } else if (isMd) {
            return 40;
        } else if (isLg) {
            return 112;
        }
    }, [isMobile, isXs, isMd, isLg]);

    return (
        <>
            <Box className={classes.smHero} />
            <Hero
                className={classes.hero}
                containerClassName={classes.heroInner}
            >
                <Typography variant="h2" className={classes.heroTitle}>
                    Perfectly Clear {!isMobile && <br />} Aligners for{' '}
                    {!isMobile && <br />} Perfect Smiles.
                </Typography>
                <Typography variant="body1" className={classes.heroText}>
                    Our dentist directed treatment and state-of-the-art
                    aligners, ensures you’ll love your smile — guaranteed.
                </Typography>
                <Button
                    variant="plain"
                    className={classes.heroButton}
                    title="Ask your Doctor"
                    arrow
                />
            </Hero>
            <Container className={classes.beforeVideo}>
                <Typography
                    variant="overline"
                    className={clsx(classes.sectionTitle, classes.centered)}
                >
                    THE ORTHOSNAP DIFFERENCE
                </Typography>
                <Typography
                    variant="h3"
                    className={clsx(classes.h3Title, classes.centered)}
                >
                    Uncommon Precision.
                    <br /> Perfect Alignment.
                </Typography>
                <Typography
                    variant="body1"
                    className={clsx(classes.text, classes.centered)}
                >
                    The precise fit and superior technology of Orthosnap
                    aligners create {!isMobile && <br />} better outcomes and
                    beautiful smiles that last. See for yourself!
                </Typography>
            </Container>
            <Container className={classes.videoContainer}>
                <VideoPlayer
                    preview={videoPreview}
                    url={process.env.GATSBY_PATIENT_VIDEO_URL as string}
                />
            </Container>
            <Container section className={classes.partnering}>
                <ContentImage
                    alt="aligners"
                    imageSrc={aligners}
                    title="How are Orthosnap aligners different?"
                    content={
                        <Typography className={classes.text} variant="body1">
                            Orthosnap’s patented process enables the aligner to
                            apply more even force across the surface area of the
                            tooth than other aligners. This process results in
                            the most efficient and effective corrective action,
                            leading to better, faster results with fewer
                            treatment steps.
                        </Typography>
                    }
                />
            </Container>
            <Container section>
                <ContentImage
                    alt="printing"
                    imageSrc={printing}
                    imageSrcWebP={printingWebP}
                    title="3D printing? No way."
                    revert
                    content={
                        <Typography className={classes.text} variant="body1">
                            Instead of being 3D printed, our state-of-the-art
                            process crafts your aligners from PVS, a more
                            affordable but higher-quality aligner material. This
                            lets you: <br />
                            <br />
                            • Get clear, striation-free aligners <br />
                            • Own the most accurate fit to the tooth in the
                            industry <br />• Save hundreds on cost and time to
                            complete your treatment
                        </Typography>
                    }
                />
            </Container>
            <Container section>
                <ContentImage
                    alt="tooth"
                    imageSrc={tooth}
                    title="Now you see them, now you don’t."
                    content={
                        <Typography className={classes.text} variant="body1">
                            Orthosnap aligners move teeth discreetly without
                            being noticed by others. Smooth edges mean greater
                            comfort, with no speech interference or irritation
                            to gums and cheeks.
                        </Typography>
                    }
                />
            </Container>
            <Container grey className={classes.worksContainer}>
                <Typography
                    variant="subtitle2"
                    className={clsx(classes.centered, classes.text)}
                >
                    How Orthosnap Works
                </Typography>
                <Grid container spacing={4} className={classes.works}>
                    {renderedWorks}
                </Grid>
            </Container>
            <Box>
                <Typography
                    variant="h3"
                    className={clsx(classes.h3Title, classes.centered)}
                >
                    Everybody Smiles with Orthosnap
                </Typography>
                <Slider
                    wrapperClassName={classes.slider}
                    firstItemIndex={1}
                    slidesMargin={slidesMargin}
                    centered
                    items={sliderItems}
                />
            </Box>
            <Container grey className={classes.affordableContainer}>
                <Box className={classes.affordable}>
                    <picture>
                        <source type="image/webp" srcSet={affordableWebP} />
                        <source type="image/png" srcSet={affordable} />
                        <img
                            src={affordable}
                            className={classes.affordableImage}
                            alt="affordableImage"
                        />
                    </picture>
                    <Box className={classes.affordableBlock}>
                        <Typography
                            variant="subtitle1"
                            className={clsx(
                                classes.text,
                                classes.affordableTitle
                            )}
                        >
                            Is Orthosnap affordable?
                        </Typography>
                        <Typography variant="body1" className={classes.text}>
                            Orthosnap makes a beautiful smile affordable. Unlike
                            other dental aligner companies which expect
                            thousands upfront, Orthosnap offers “Step Pricing” -
                            only pay for the aligners you use.
                        </Typography>
                    </Box>
                </Box>
            </Container>
        </>
    );
};
